import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { MAX_ENTRY_CHARS, promptForVerse } from "@/lib/journal";

export const JournalModal = ({ verse, onClose, onSave }) => {
  const [text, setText] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => textareaRef.current?.focus(), 150);
    return () => clearTimeout(id);
  }, []);

  if (!verse) return null;

  const remaining = MAX_ENTRY_CHARS - text.length;
  const canSave = text.trim().length > 0;
  const prompt = promptForVerse(verse);

  const handleSave = () => {
    if (!canSave) return;
    onSave(text.trim());
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="absolute inset-0 z-40 flex items-end md:items-center justify-center px-4 pb-6 md:pb-0"
      data-testid="journal-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(44, 42, 40, 0.32)", backdropFilter: "blur(2px)" }}
        onClick={onClose}
      />

      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        className="relative w-full max-w-md rounded-2xl bg-[#F5F3ED] border border-[#E5E1D8] shadow-sm"
      >
        <div className="paper-grain absolute inset-0 rounded-2xl pointer-events-none" />

        <div className="relative z-10 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-ui text-[10px] uppercase tracking-[0.32em] text-[#96918A]">
                A reflection on
              </p>
              <p className="font-serif-verse italic text-base md:text-lg text-[#4A5D4E] mt-1 truncate">
                {verse.reference}
                <span className="font-ui not-italic text-[10px] text-[#96918A] uppercase tracking-[0.2em] ml-2">
                  {verse.translation}
                </span>
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="journal-close"
              aria-label="Close"
              className="rounded-full p-1 -mr-1 text-[#6A6661] hover:bg-[#EAE6DF] transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          <p
            className="mt-5 font-serif-verse text-[#2C2A28] text-base md:text-lg leading-relaxed"
            data-testid="journal-prompt"
          >
            {prompt}
          </p>

          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_ENTRY_CHARS))}
            placeholder="One or two honest sentences…"
            rows={4}
            data-testid="journal-textarea"
            className="mt-4 w-full resize-none rounded-xl bg-[#EAE6DF]/40 border border-[#E5E1D8] focus:border-[#4A5D4E] focus:outline-none p-4 font-serif-verse text-[#2C2A28] text-base leading-relaxed placeholder:text-[#96918A]/70 placeholder:italic placeholder:font-ui placeholder:text-sm transition-colors"
          />

          <div className="mt-3 flex items-center justify-between">
            <span
              className={[
                "font-ui text-[10px] uppercase tracking-[0.2em]",
                remaining < 30 ? "text-[#6A6661]" : "text-[#96918A]",
              ].join(" ")}
              data-testid="journal-remaining"
            >
              {remaining} left
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                data-testid="journal-cancel"
                className="rounded-full px-4 py-2 font-ui text-[11px] uppercase tracking-[0.22em] text-[#6A6661] hover:text-[#2C2A28] transition-colors"
              >
                Cancel
              </button>
              <motion.button
                whileTap={{ scale: canSave ? 0.95 : 1 }}
                onClick={handleSave}
                disabled={!canSave}
                data-testid="journal-save"
                className="rounded-full px-5 py-2 font-ui text-[11px] uppercase tracking-[0.22em] bg-[#4A5D4E] text-[#F5F3ED] hover:bg-[#3B4C3F] transition-colors disabled:opacity-40"
              >
                Save
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
